import { translate } from "@escapio/shared/l10n/translate";

export class Validator extends HTMLElement {
	connectedCallback() {
		this.form = this.querySelector("form");
		this.form.addEventListener("submit", e => this.onSubmit(e));

		this.inputNodes = Array.from(this.form.elements).filter(e =>
			Boolean(e.name),
		);

		this.inputNodes.forEach(e =>
			e.addEventListener("blur", () =>
				this.send([e, ...this.getDependencies(e)], true),
			),
		);
		this.superErrorNode = this.querySelector("[data-form-error-message]");
		this.disableForm(false);
	}

	async onSubmit(e) {
		e.preventDefault();
		this.disableForm(true);
		const nonceResponse = await fetch("/form-nonce");
		const { nonce } = await nonceResponse.json();
		await this.send(this.inputNodes, false, nonce);
		this.disableForm(false);
	}

	async send(inputNodes, validateOnly, nonce) {
		if (this.abortController) {
			this.abortController.abort();
		}
		this.abortController = new AbortController();

		const formData = new FormData();

		inputNodes.forEach(input => {
			if (["checkbox", "radio"].includes(input.type) && !input.checked) {
				return;
			}
			formData.append(input.name, input.value);
		});

		const url = this.getFormUrl();
		if (validateOnly) {
			url.searchParams.set("ajax-form-validate-only", 1);
		}

		const response = await fetch(url, {
			method: this.form.method,
			body: formData,
			credentials: "include",
			signal: this.abortController.signal,
			...(nonce
				? {
						headers: {
							"X-Form-Nonce": nonce,
						},
					}
				: {}),
		});
		this.abortController = null;

		this.resetErrors(inputNodes);
		if (!response.ok) {
			if (response.status === 404) {
				return this.showSuperError(translate("404_title"));
			}
			if (response.status === 500) {
				return this.showSuperError(translate("error_500_message"));
			}
			// Show field related errors.
			const responseBody = await response.json();
			return this.renderFieldErrors(responseBody, response.status);
		}
		if (validateOnly) {
			return;
		}
		return this.onSuccess();
	}

	getDependencies(input) {
		const attr = input.getAttribute("data-depends-on");
		return (attr ? attr.split(",") : []).map(name => this.form.elements[name]);
	}

	getFormUrl() {
		return new URL(this.form.action);
	}

	getContainerNodeFor(inputNode) {
		let parentNode = inputNode.parentNode;

		// Ignore label and go one level higher to the container node.
		if (parentNode.tagName === "LABEL") {
			return parentNode.parentNode;
		}

		return parentNode;
	}

	renderFieldErrors(errors) {
		Object.entries(errors).forEach(([name, fieldErrors]) => {
			const containerNode = this.getContainerNodeFor(this.form[name]);

			containerNode.classList.add("error");
			const errorNode = containerNode.querySelector("[data-form-field-error]");
			errorNode.innerText = fieldErrors.toString();
		});
	}

	hideSuperError() {
		if (this.superErrorNode) {
			this.superErrorNode.classList.add("hidden");
		}
	}

	showSuperError(errorMessage) {
		if (this.superErrorNode) {
			this.superErrorNode.classList.remove("hidden");
			this.superErrorNode.querySelector(
				"[data-form-error-text-node]",
			).innerText = errorMessage;
		}
	}

	onSuccess() {
		const successMessageNode = this.querySelector(
			"[data-form-success-message]",
		);
		if (successMessageNode) {
			successMessageNode.classList.remove("hidden");
		}
		if (this.hasAttribute("one-time-submit")) {
			Array.from(this.children)
				.filter(child => !child.hasAttribute("data-form-success-message"))
				.forEach(node => node.classList.add("hidden"));
		}
		this.dispatchEvent(new CustomEvent("successfulSubmit"));
	}

	resetErrors(inputNodes) {
		this.hideSuperError();
		inputNodes.forEach(inputNode => {
			const containerNode = this.getContainerNodeFor(inputNode);
			containerNode.classList.remove("error");
			const errorNode = containerNode.querySelector("[data-form-field-error]");
			if (errorNode) {
				errorNode.innerText = "";
			}
		});
	}

	disableForm(disabled) {
		Array.from(this.inputNodes)
			.concat([...this.querySelectorAll("[type='submit']")])
			.forEach(node => (node.disabled = disabled ? "disabled" : ""));
	}
}
customElements.define("esc-form-validator", Validator);
